import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { StockRow } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import { useAuth } from 'context/AuthContext';
import { useGlobal } from 'context/GlobalContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import { canWarehouseOps } from 'utils/helpers/access/hasUserAccess';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useWarehouse } from '../context/WarehouseProvider';
import type { StockLotRow } from '../OffStockLots/StockLotsTable/types';
import { getWarehouseHeader } from './getWarehouseHeader';

type StocksResponse = {
    stocks?: any[];
    balance?: number | string;
    credit?: number | string;
    warehouse?: any;
    warehouseEmpty?: boolean | null;
    paginator?: any;
} | null;

export const useWarehousePage = (wId: number) => {
    const { t } = useTranslation(['Warehouses']);
    const { g } = useGlobal();
    const { auth } = useAuth();
    const { getSettings } = useSettings();
    const { showToast } = useToast();
    const {
        stocksOrder,
        stocksDesc,
        showSpinner,
        setShowSpinner,
        stockFilter,
        setStockFilter,
        showCredit,
        setStocksOrder,
        setStocksDesc,
        listResetNonce,
        listRefreshNonce,
        showLotBreakdown,
    } = useWarehouse();

    const [stocksList, setStocksList] = useState<any[] | null>(null);
    const [warehouseInfo, setWarehouseInfo] = useState<any>(null);
    const [page, setPage] = useState<number | null>(null);
    const [showComing, setShowComing] = useState<boolean | number>(false);
    const [showTransfer, setShowTransfer] = useState<boolean | number | 'all'>(false);
    const [showFilter, setShowFilter] = useState(false);
    const [balance, setBalance] = useState(0);
    const [credit, setCredit] = useState(0);
    const [teamId, setTeamId] = useState<number | null>(null);
    const [teamName, setTeamName] = useState('');
    const [teamDeleted, setTeamDeleted] = useState(false);
    const [customId, setCustomId] = useState<number | null>(null);
    const [customName, setCustomName] = useState('');
    const [customDeleted, setCustomDeleted] = useState(false);
    const [warehouseEmpty, setWarehouseEmpty] = useState<boolean | null>(null);
    const [prevFilter, setPrevFilter] = useState<Record<string, any> | null>(stockFilter);
    const [showPaymentsId, setShowPaymentsId] = useState<number | null>(null);
    const [showPaymentsTitle, setShowPaymentsTitle] = useState<string | null>(null);
    const [showStockLots, setShowStockLots] = useState<StockRow | null>(null);
    const [stockLots, setStockLots] = useState<Record<number, StockLotRow[]> | null>(null);
    const [seenWId, setSeenWId] = useState(wId);
    const [stocksVersion, setStocksVersion] = useState(0);
    const [stockLotsSource, setStockLotsSource] = useState<any[] | null>(null);

    const location = useLocation();
    const onPage = useListOnPage();
    const prevStockData = useRef<string | null>(null);
    const inflightWId = useRef<number | null>(null);
    const canOps = canWarehouseOps(auth?.user);

    if (seenWId !== wId) {
        setSeenWId(wId);
        setStocksList(null);
        setStockLots(null);
        setStockLotsSource(null);
        setWarehouseInfo(null);
        setBalance(0);
        setCredit(0);
        setTeamId(null);
        setTeamName('');
        setTeamDeleted(false);
        setCustomId(null);
        setCustomName('');
        setCustomDeleted(false);
        setWarehouseEmpty(null);
        setStocksVersion(0);
        prevStockData.current = null;
    } else if (showLotBreakdown && stocksList !== stockLotsSource) {
        setStockLotsSource(stocksList);
        setStockLots(null);
    }

    const applyStocksResponse = useCallback((resStock: NonNullable<StocksResponse>) => {
        setStocksList(resStock.stocks ?? []);
        setBalance(Number(resStock?.balance) || 0);
        setCredit(Number(resStock?.credit) || 0);
        setWarehouseInfo(resStock?.warehouse);
        setTeamId(resStock?.warehouse?.team?.ID || null);
        setTeamName(resStock?.warehouse?.team?.teamName || '');
        setTeamDeleted(!!Number(resStock?.warehouse?.team?.deleted));
        setCustomId(resStock?.warehouse?.custom?.ID || resStock?.warehouse?.customId || null);
        setCustomName(resStock?.warehouse?.custom?.name || '');
        setCustomDeleted(!!Number(resStock?.warehouse?.custom?.deleted));
        setWarehouseEmpty(resStock?.warehouseEmpty ?? null);
        g.setPaginator((prev: any) => ({
            ...prev,
            [location.pathname]: resStock.paginator,
        }));
        setStocksVersion((version) => version + 1);
    }, [g, location.pathname]);

    const handleShowFilter = (show: boolean) => {
        if (show) setPrevFilter(stockFilter);
        setShowFilter(show);
    };

    const fetchStocks = async (force = false) => {
        if (force) prevStockData.current = null;
        if (!page) return null;
        if (showSpinner && inflightWId.current === wId) return null;

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const stockData = {
            search,
            page: (page < 0) ? 1 : page || 1,
            onPage,
            order: `${stocksOrder ? `order by ${stocksOrder} ` : ''} ${stocksDesc ? 'DESC' : ''}`,
            ...stockFilter,
        };

        // Запобіжник для подвійного запиту
        if (prevStockData.current == JSON.stringify({ ...stockData, wId })) return;
        const requestWId = wId;
        inflightWId.current = requestWId;
        setShowSpinner(true);
        const resStock = await apiWarehouse.getStocks(requestWId, stockData);
        if (inflightWId.current !== requestWId) return;
        inflightWId.current = null;
        setShowSpinner(false);
        if (!resStock) return showToast(t('common:messError'), 'danger');
        applyStocksResponse(resStock);
    };

    const resetStocksList = useCallback(async () => {
        prevStockData.current = null;
        setStocksOrder('');
        setStocksDesc(false);
        setStockFilter(null);
        setPrevFilter(null);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        setShowSpinner(true);
        const resStock = await apiWarehouse.getStocks(wId, {
            search,
            page: 1,
            onPage,
            order: '',
        });
        setShowSpinner(false);
        if (!resStock) return showToast(t('common:messError'), 'danger');
        applyStocksResponse(resStock);
    }, [
        applyStocksResponse,
        g,
        location.pathname,
        onPage,
        setShowSpinner,
        setStockFilter,
        setStocksDesc,
        setStocksOrder,
        showToast,
        t,
        wId,
    ]);

    useEffect(() => {
        if (!showLotBreakdown) {
            setStockLots(null);
            return;
        }
        if (!stocksList) return;

        const productIds = stocksList
            .map((row) => Number(row?.productId || 0))
            .filter((id) => id > 0);
        if (!productIds.length) {
            setStockLots({});
            return;
        }

        let cancelled = false;

        const load = async () => {
            const res = await apiWarehouse.getStockLots(wId, productIds);
            if (cancelled) return;
            if (!Array.isArray(res)) {
                showToast(t('common:messError'), 'danger');
                setStockLots({});
                return;
            }

            const next: Record<number, StockLotRow[]> = {};
            for (const id of productIds) next[id] = [];
            for (const item of res) {
                const productId = Number(item?.productId || 0);
                if (!productId) continue;
                const lots = Array.isArray(item?.lots) ? item.lots : [];
                next[productId] = lots.map((lot: StockLotRow) => ({
                    productId,
                    operPrice: lot?.operPrice == null ? null : Number(lot.operPrice),
                    operPriceRate: lot?.operPriceRate == null ? null : Number(lot.operPriceRate),
                    stock: Number(lot?.stock || 0),
                }));
            }
            setStockLots(next);
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [showLotBreakdown, stocksList, showToast, t, wId]);

    useListRefreshEffect(listResetNonce, () => {
        void resetStocksList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevStockData.current = null;
        void fetchStocks(true);
    });

    // Хук на пошук
    useEffect(() => {
        if (page == 1) fetchStocks();
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    // Змінюємо "внутрішню сторінку" при зміні пагінатора
    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page != _page) setPage(_page);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
    ]);

    // Хук на зміну сторінки
    useEffect(() => {
        fetchStocks();
    }, [page, wId, stocksOrder, stocksDesc, stockFilter, onPage]);

    const handleTeamChanged = async () => {
        await getSettings();
        await fetchStocks(true);
    };

    const warehouseHeader = getWarehouseHeader({
        wId,
        warehouseInfo,
        teamId,
        teamName,
        customId,
        customName,
        t,
    });

    return {
        t,
        auth,
        canOps,
        showCredit,
        stocksList,
        warehouseInfo,
        warehouseHeader,
        balance,
        credit,
        teamId,
        teamName,
        teamDeleted,
        customId,
        customName,
        customDeleted,
        warehouseEmpty,
        showComing,
        setShowComing,
        showTransfer,
        setShowTransfer,
        showFilter,
        handleShowFilter,
        prevFilter,
        setPrevFilter,
        showPaymentsId,
        setShowPaymentsId,
        showPaymentsTitle,
        setShowPaymentsTitle,
        showStockLots,
        setShowStockLots,
        stockLots,
        fetchStocks,
        stocksVersion,
        handleTeamChanged,
    };
};
